/**
 * Bot Config Service
 * Generic CRUD for custom LLM bot configurations
 */

import { createBotConfig } from './bot-config.js'

interface StorageAdapter {
  get(key: string): Promise<string | null>
  set(key: string, value: string): Promise<void>
}

interface BotConfigServiceOptions {
  storageKey: string
  /**
   * When true, on the first `loadBots()` call check `localStorage[storageKey]`
   * and migrate it into the storage adapter (only if the adapter has no value
   * yet). One-shot, idempotent. Used by web entry to seed e2e specs that pre-
   * populate `localStorage` via `addInitScript` before the IndexedDB-backed
   * adapter mounts. Pure DI — no `window` reference is read here unless the
   * runtime exposes `globalThis.localStorage`.
   */
  seedFromLocalStorage?: boolean
}

declare const localStorage: any

export function createBotConfigService(
  storageAdapter: StorageAdapter,
  { storageKey, seedFromLocalStorage = false }: BotConfigServiceOptions,
) {
  let seeded = false
  const maybeSeed = async () => {
    if (seeded || !seedFromLocalStorage) return
    seeded = true
    try {
      if (typeof localStorage === 'undefined' || !localStorage) return
      const fromLs = localStorage.getItem(storageKey)
      if (!fromLs) return
      const existing = await storageAdapter.get(storageKey)
      if (existing) return
      await storageAdapter.set(storageKey, fromLs)
    } catch (err: any) {
      console.warn('[BotConfigService] seedFromLocalStorage failed:', err?.message || err)
    }
  }

  const loadBots = async () => {
    await maybeSeed()
    const stored = await storageAdapter.get(storageKey)
    if (!stored) return []
    try {
      return JSON.parse(stored)
    } catch (err: any) {
      console.warn('Failed to parse bot config from storage, resetting:', err.message)
      return []
    }
  }

  const saveBots = async (bots: any[]) => {
    await storageAdapter.set(storageKey, JSON.stringify(bots))
  }

  const getBots = async () => {
    return await loadBots()
  }

  const getBot = async (botId: string) => {
    const bots = await loadBots()
    return bots.find((b: any) => b.botId === botId) || null
  }

  const createBot = async (config: any) => {
    const bot = createBotConfig(config)
    const bots = await loadBots()
    bots.push(bot)
    await saveBots(bots)
    return bot
  }

  const updateBot = async (botId: string, updates: any) => {
    const bots = await loadBots()
    const idx = bots.findIndex((b: any) => b.botId === botId)
    if (idx < 0) throw new Error('Bot not found')
    bots[idx] = { ...bots[idx], ...updates, botId }
    await saveBots(bots)
    return bots[idx]
  }

  const deleteBot = async (botId: string) => {
    const bots = await loadBots()
    await saveBots(bots.filter((b: any) => b.botId !== botId))
  }

  return { getBots, getBot, createBot, updateBot, deleteBot }
}
